/**
 * Android device-state pre-flight + recovery helpers for the WebXR AR launch.
 *
 * The microsite shows "Launch AR" on any touch device with no capability
 * pre-check, so users on a browser that can't run WebXR AR (an in-app webview,
 * Firefox, Samsung Internet) or with a missing/stale ARCore previously fell
 * into the launch flow and hit a silent or cryptic failure. These helpers let
 * the launch path detect the common, recoverable cases up front and hand the
 * user a concrete next step (open in Chrome / install Google Play Services for
 * AR / retry) rather than a dead spinner.
 *
 * iOS is handled separately (it redirects to the App Clip before this runs), so
 * everything here is Android/desktop-web oriented.
 */

const ua = (): string => (typeof navigator !== "undefined" ? navigator.userAgent : "");

export function isAndroid(): boolean {
  return /Android/i.test(ua());
}

/**
 * In-app webview (opened inside Instagram, Facebook, TikTok, etc.). These
 * Chromium-backed webviews do NOT expose WebXR and often can't grant the
 * camera, so AR can't run — the user has to escape to a real browser.
 */
export function isInAppBrowser(): boolean {
  const s = ua();
  // Android WebView marker ("; wv)") plus the common social-app tokens. We
  // match the specific "wv)" form rather than a bare "wv" — a false positive
  // here HARD-blocks a real Chrome user from AR entirely, so the regex must not
  // catch a normal Chrome UA. (Standard Android Chrome has no "wv" token.)
  return /\bwv\)|FBAN|FBAV|FB_IAB|Instagram|Line\/|Snapchat|musical_ly|TikTok|Bytedance|Twitter|Pinterest|LinkedInApp|WhatsApp|GSA\//i.test(s);
}

/**
 * Android browser that can actually run immersive-ar WebXR. Must be Chromium
 * (Chrome / Brave / Edge all carry the `Chrome/` token and support it) and NOT
 * one of the Chromium-skinned browsers that don't ship immersive-ar (Samsung
 * Internet) or the non-Chromium ones (Firefox, Opera, UC, Yandex, MIUI).
 */
export function isWebXRCapableAndroidBrowser(): boolean {
  const s = ua();
  if (!/Chrome\/\d/.test(s)) return false;
  if (/SamsungBrowser/i.test(s)) return false;
  if (/(?:Firefox|FxiOS|OPR\/|Opera|UCBrowser|YaBrowser|MiuiBrowser|HuaweiBrowser)/i.test(s)) return false;
  return true;
}

export type ArBlockerKind = "in-app-browser" | "wrong-browser";

/**
 * Synchronous, UA-only pre-flight for the Android launch path. Returns a
 * blocker kind when AR definitely can't run in this browser (so we show a
 * recovery message instead of the spinner), or null to proceed. Deliberately
 * conservative — it only blocks browsers that are KNOWN not to support
 * immersive-ar, so a capable-but-unusual Chromium browser is never false-
 * blocked. Missing/stale ARCore is NOT pre-flighted here (Chrome handles the
 * install at requestSession time, and a genuine failure surfaces with an
 * "install Google Play Services for AR" recovery action afterwards).
 */
export function detectArBlocker(): ArBlockerKind | null {
  // Only Android has these browser-state failure modes on the WebXR path.
  // iOS is redirected to the App Clip before this runs; desktop falls through
  // to the normal not-supported handling.
  if (!isAndroid()) return null;
  if (isInAppBrowser()) return "in-app-browser";
  if (!isWebXRCapableAndroidBrowser()) return "wrong-browser";
  return null;
}

export type CameraStatus = "ok" | "denied" | "busy" | "notfound";

/**
 * Map a getUserMedia rejection to an actionable status. The big one we were
 * conflating before is `NotReadableError` (a.k.a. TrackStartError / AbortError)
 * — the camera hardware is held by another app or tab — which needs "close the
 * other app", not "grant permission".
 */
export function classifyCameraError(err: unknown): Exclude<CameraStatus, "ok"> {
  const name = (err as { name?: string })?.name ?? "";
  switch (name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
    case "SecurityError":
      return "denied";
    case "NotReadableError":
    case "TrackStartError":
    case "AbortError":
      return "busy";
    case "NotFoundError":
    case "DevicesNotFoundError":
    case "OverconstrainedError":
    case "ConstraintNotSatisfiedError":
      return "notfound";
    default:
      // Unknown — treat as a permission problem, the safest existing default.
      return "denied";
  }
}

/** Play Store listing for Google Play Services for AR (ARCore). */
export const ARCORE_PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.google.ar.core";

/** Open the ARCore Play Store page so the user can install / update it. */
export function openArCoreUpdate(): void {
  try {
    window.location.href = ARCORE_PLAY_STORE_URL;
  } catch {
    /* best effort */
  }
}

/**
 * Re-open the current page in Chrome. From an in-app webview an Android
 * `intent://` URL with the Chrome package hands the URL to Chrome directly;
 * a plain Chromium-but-wrong browser may ignore it, in which case the on-screen
 * copy ("tap ⋯ → Open in Chrome") is the fallback.
 */
export function openInChrome(): void {
  try {
    const href = window.location.href;
    const withoutScheme = href.replace(/^https?:\/\//, "");
    window.location.href = `intent://${withoutScheme}#Intent;scheme=https;package=com.android.chrome;end`;
  } catch {
    /* best effort — the message tells the user how to do it manually */
  }
}
